import { defineStore } from 'pinia'
import axios from 'axios'
import { useShoppingCartStore } from '@/stores/shoppingCart'
import {
  DL_PUBLIC_ENDPOINT,
  OD_PUBLIC_ENDPOINT,
  VS_PUBLIC_ENDPOINT,
  EXAM_REPORT_ENDPOINT,
  DRIVER_ENDPOINT,
  VEHICLE_ENDPOINT,
  IDENTITY_PROVIDER_SERVICE_ENDPOINT,
} from '@/constants/Endpoints'

import { LOGGER } from '@/util/logger'
import { ref, computed } from 'vue'
import { getRouter } from '@/router'
import { useWorkflowInProgressStore } from './workflowInProgress'
import { useSnackbarStore } from './snackbar'
import { useCompletedTransactionStore } from '@/stores/completedTransaction'
import { useConfigStore } from './config'

const POPUP_THRESHOLD = 5 * 60 // 5 minute threshold

export const useUserLogin = defineStore('userLogin', () => {
  const shoppingCart = useShoppingCartStore()
  const inProgressStore = useWorkflowInProgressStore()
  const snackbarStore = useSnackbarStore()
  const configStore = useConfigStore()
  const loginResponses = ref(new Map())
  const vsLoginResponses = ref(new Map())
  const currentToken = ref(null)
  const currentDlNumber = ref(null)
  const currentTitleNumber = ref(null)
  const odLoginResponse = ref(null)
  const preVerifyLoginId = ref(null)
  const esLoginResponse = ref(null)
  const sessionExpiry = ref(null)
  const secondsRemaining = ref(0)
  const showTimeoutPopup = ref(false)
  let interval = null
  const completedTransactionStore = useCompletedTransactionStore()
  let sessionExtensionCount = ref(0)
  const MAX_SESSION_EXTENSION_LIMIT = 10

  const loginResponse = computed(() => {
    if (currentDlNumber.value) {
      return loginResponses.value.get(currentDlNumber.value) ?? null
    }
    return null
  })

  const vsLoginResponse = computed(() => {
    if (currentTitleNumber.value) {
      return vsLoginResponses.value.get(currentTitleNumber.value) ?? null
    }
    return null
  })

  const getLoginResponse = (key) => {
    const dl = loginResponses.value.get(key)
    if (dl) return dl

    const vs = vsLoginResponses.value.get(key)
    if (vs) return vs

    return null
  }

  const loginDL = async (payload) => {
    inProgressStore.clearAll()
    try {
      if (shoppingCart?.cartItems?.length <= 0) {
        loginResponses.value.clear()
        vsLoginResponses.value.clear()
        completedTransactionStore.clear()
      } else {
        const loggedInUserInfo = userAlreadyLoggedIn(payload?.idNumber)
        //check if idNumber is already in the login responses
        if (loggedInUserInfo && loggedInUserInfo.olNum === payload.idNumber) {
          return loggedInUserInfo
        }
        //if there are already items in the cart, this login is an additional service with different ID number. Set transaction ID so the transaction id gets updated
        //first check is there is a valid DL login to get the transaction id from
        const keys = [...loginResponses.value.keys()]
        if (keys.length > 0) {
          const firstKey = keys[0] // get the first response from the Map(all responses will have same transactionID)
          payload.transactionID = loginResponses.value.get(firstKey).transactionOutputDTO.transactionID
        }
        //if no DL login transaction id, check for a valid VS transaction id
        else {
          const firstLoginResponseKey = vsLoginResponses.value.keys().next().value // get the first response from the Map(all responses will have same transactionID)
          payload.transactionID = vsLoginResponses.value.get(firstLoginResponseKey).transactionOutputDTO.transactionID
        }
      }
      const response = await axios.post(DL_PUBLIC_ENDPOINT.LOGIN, payload, { withCredentials: true })
      const { clientDriverDTO, token } = response.data.data
      const dlNumber = clientDriverDTO.olNum

      loginResponses.value.set(dlNumber, clientDriverDTO)
      // save the token
      currentToken.value = token
      currentDlNumber.value = dlNumber
      return clientDriverDTO
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }

  const loginVS = async (payload, loginType) => {
    inProgressStore.clearAll()

    try {
      if (shoppingCart?.cartItems?.length <= 0) {
        loginResponses.value.clear()
        vsLoginResponses.value.clear()
        completedTransactionStore.clear()
      } else {
        const loggedInUserInfo = userAlreadyLoggedIn(payload?.idNumber)
        //check if idNumber is already in the login responses
        if (loggedInUserInfo && loggedInUserInfo.olNum === payload.idNumber) {
          return loggedInUserInfo
        }
        //if there are already items in the cart, this login is an additional service with different ID number. Set transaction ID so the transaction id gets updated
        //first check is there is a valid VS login to get the transaction id from
        const keys = [...vsLoginResponses.value.keys()]
        if (keys.length > 0) {
          const firstKey = keys[0] // get the first response from the Map(all responses will have same transactionID)
          payload.transactionID = vsLoginResponses.value.get(firstKey).transactionOutputDTO.transactionID
        }
        //if no VS login transaction id, check for a valid DL transaction id
        else {
          const firstLoginResponseKey = loginResponses.value.keys().next().value // get the first response from the Map(all responses will have same transactionID)
          payload.transactionID = loginResponses.value.get(firstLoginResponseKey).transactionOutputDTO.transactionID
        }
      }
      // loginType either plate or vin
      const response =
        loginType == 'Log in with VIN'
          ? await axios.post(VS_PUBLIC_ENDPOINT.LOGIN_VIN, payload, { withCredentials: true })
          : await axios.post(VS_PUBLIC_ENDPOINT.LOGIN_PLATE, payload, { withCredentials: true })
      const data = response.data.data
      const vsNumber = data.clientVehicleDTO.titleNum || data.clientVehicleDTO.legacyTitleNum
      vsLoginResponses.value.set(vsNumber, data.clientVehicleDTO)
      // save the token
      currentToken.value = data.token
      currentTitleNumber.value = vsNumber
      await configStore.loadVehicleRenewalConfig()
      return response
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }

  const userAlreadyLoggedIn = (idNumber) => {
    const loggedInUserInfo = loginResponses.value.get(idNumber)
    if (loggedInUserInfo) {
      currentDlNumber.value = idNumber
    }
    return loggedInUserInfo
  }

  const loginES = async (payload) => {
    try {
      const response = await axios.post(EXAM_REPORT_ENDPOINT.LOGIN, payload, { withCredentials: true })
      esLoginResponse.value = response.data
      // save the token
      currentToken.value = esLoginResponse.value.data?.token
      return esLoginResponse.value
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }

  const fetchExams = async () => {
    const loginData = esLoginResponse.value?.data

    if (!loginData?.eligibleExams?.length) {
      esLoginResponse.value = {
        ...esLoginResponse.value,
        scheduledExams: loginData?.scheduledExams ?? [],
        eligibleExams: [],
        customer: loginData ?? null,
      }
      return null
    }
    try {
      const payload = {
        idNumber: esLoginResponse.value?.data?.idNumber,
        transactionID: esLoginResponse.value?.data?.transactionOutputDTO?.transactionID,
      }
      const response = await axios.post(EXAM_REPORT_ENDPOINT.EXAMS, payload)
      const data = response.data.data
      esLoginResponse.value = {
        ...esLoginResponse.value,
        scheduledExams: data?.scheduledExams ?? [],
        eligibleExams: data?.eligibleExams ?? [],
        customer: data?.customer ?? esLoginResponse.value?.data ?? null,
      }
      return response
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }

  const setLoginResponses = (thisDriver, transactionID) => {
    Object.keys(thisDriver).forEach((i) => {
      const loginInfo = thisDriver[i]
      const transactionObj = i == currentDlNumber.value ? { transactionOutputDTO: { transactionID } } : {}
      const login = { ...loginInfo, ...transactionObj }
      loginResponses.value.set(i, login)
    })
  }

  /**Login Organ Donor Designation standalone */
  const loginOD = async (payload) => {
    try {
      const response = await axios.post(OD_PUBLIC_ENDPOINT.LOGIN, payload, { withCredentials: true })
      const { clientDriverDTO, token } = response.data.data
      odLoginResponse.value = { ...clientDriverDTO }
      // save the token
      currentToken.value = token
      const dlNumber = clientDriverDTO.olNum
      loginResponses.value.set(dlNumber, clientDriverDTO)
      return response
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }
  /**Update Organ Donor designation(standalone) */
  const updateOD = async (payload) => {
    try {
      const response = await axios.post(OD_PUBLIC_ENDPOINT.UPDATE, payload)
      return response
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }

  const fetchRestorationFee = async () => {
    try {
      const current = loginResponse.value
      const payload = {
        dlNumber: current.olNum,
        transactionID: current.transactionOutputDTO.transactionID,
        serviceType: 'DLRESTORFEE',
      }

      const response = await axios.post(DRIVER_ENDPOINT.FEE_CALC, payload)

      return response.data.data
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }

  const fetchRestorationLetter = async () => {
    try {
      const current = loginResponse.value
      const payload = {
        dlNumber: current.olNum,
        transactionID: current.transactionOutputDTO.transactionID,
      }
      const response = await axios.post(DRIVER_ENDPOINT.FETCH_RESTORATION_LETTER, payload)
      return response.data
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }

  const fetchLocalUseFee = async () => {
    try {
      const current = vsLoginResponse.value
      const payload = {
        transactionID: current.transactionOutputDTO?.transactionID,
        idNumber: current.legacyTitleNum ?? current.plate?.legacyTitleNum,
        serviceType: 'VRLOCALFEE',
      }

      const response = await axios.post(VEHICLE_ENDPOINT.FEE_CALC, payload)

      return response.data.data
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }

  const fetchVehicleRestorationLetter = async () => {
    try {
      const current = vsLoginResponse.value
      const payload = {
        titleNumber: current?.legacyTitleNum,
        transactionID: current.transactionOutputDTO?.transactionID,
      }

      const response = await axios.post(VEHICLE_ENDPOINT.FETCH_RESTORATION_LETTER, payload)
      return response.data
    } catch (e) {
      LOGGER.logException(e)
      throw e
    }
  }

  const startSession = () => {
    if (interval) {
      clearInterval(interval)
    }
    const SESSION_DURATION = (Number.parseInt(configStore.envConfiguration?.SessionTimeout) || 30) * 60 // 30 minute session
    sessionExpiry.value = Date.now() + SESSION_DURATION * 1000
    showTimeoutPopup.value = false

    interval = setInterval(() => {
      const remaining = Math.round((sessionExpiry.value - Date.now()) / 1000)
      secondsRemaining.value = Math.max(0, remaining)
      showTimeoutPopup.value = remaining <= POPUP_THRESHOLD && remaining > 0

      if (remaining <= 0) {
        clearInterval(interval)
        logout()
      }
    }, 1000)
  }

  const continueSession = async () => {
    if (sessionExtensionCount.value < MAX_SESSION_EXTENSION_LIMIT) {
      sessionExtensionCount.value++
      await refreshToken()
      startSession()
    } else {
      LOGGER.logException('Extension limit reached. You cannot extend further.')
    }
  }

  const logout = () => {
    const router = getRouter()
    clearInterval(interval)
    sessionExpiry.value = null
    secondsRemaining.value = 0
    showTimeoutPopup.value = false
    loginResponses.value.clear()
    vsLoginResponses.value.clear()
    currentDlNumber.value = null
    currentTitleNumber.value = null
    odLoginResponse.value = null
    esLoginResponse.value = null
    currentToken.value = null
    inProgressStore.clearAll()
    shoppingCart.clearCart()
    snackbarStore.clear()
    preVerifyLoginId.value = null

    router.push({ name: 'DLLogout' })
    deleteRefreshToken()
  }

  const refreshToken = async () => {
    const response = await axios.get(IDENTITY_PROVIDER_SERVICE_ENDPOINT.REFRESH_TOKEN, {
      withCredentials: true,
    })
    // save the token
    const data = response.data.data
    currentToken.value = data.accessToken
    currentDlNumber.value = data.idNumber
    return data
  }

  const deleteRefreshToken = async () => {
    await axios.delete(IDENTITY_PROVIDER_SERVICE_ENDPOINT.REFRESH_TOKEN)
  }

  const removeTimer = () => clearInterval(interval)

  return {
    loginDL,
    loginES,
    loginVS,
    fetchExams,
    loginResponse,
    loginResponses,
    vsLoginResponse,
    vsLoginResponses,
    currentDlNumber,
    currentTitleNumber,
    getLoginResponse,
    fetchRestorationFee,
    loginOD,
    odLoginResponse,
    esLoginResponse,
    updateOD,
    logout,
    startSession,
    continueSession,
    secondsRemaining,
    showTimeoutPopup,
    fetchRestorationLetter,
    fetchVehicleRestorationLetter,
    fetchLocalUseFee,
    currentToken,
    preVerifyLoginId,
    removeTimer,
    setLoginResponses,
    refreshToken,
  }
})
